import React, { useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Container,
    TextField,
    Typography,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import { useSearchParams } from 'react-router-dom';
import { analytics, AnalyticsEvent } from '../services/analytics';
import { sendContactEmail } from '../services/contactEmail';
import { MAIN_BG_COLOR } from '../theme';

const PAGE = {
    fontHeading: '"DM Serif Text", serif',
    fontBody: '"Inter", sans-serif',
    sectionPaddingX: '1.25rem',
    pageBg: '#f3f3f4',
} as const;

const CONTACT_SOURCE = 'Agenda una demo';
const VERIFICATION_INTENT = 'verificacion';
const VERIFICATION_MESSAGE_PREFILL =
    'Quiero pedir una verificación de menciones y audiencia con un tercero independiente.';

const DEMO_BENEFITS = [
    {
        title: 'Recorrido a tu medida',
        description:
            'Vemos juntos la plataforma con el foco en lo que tu equipo necesita medir: menciones, audiencia, livestreams o el mercado completo.',
    },
    {
        title: 'Datos reales, no slides',
        description:
            'Trabajamos con contenido y métricas actuales de tu programa o categoría, para que la conversación parta desde evidencia concreta.',
    },
    {
        title: 'Cómo se usa en la industria',
        description:
            'Te mostramos cómo productoras, marcas y agencias usan Podscope para reportar, negociar y seguir el ecosistema semana a semana.',
    },
    {
        title: 'Espacio para tus preguntas',
        description:
            'Dejamos tiempo para profundizar en casos específicos, integraciones y el flujo de trabajo que tendría tu equipo.',
    },
] as const;

const fieldSx = {
    '& .MuiOutlinedInput-root': {
        backgroundColor: '#f7f7f8',
        borderRadius: '10px',
        '& fieldset': { borderColor: 'rgba(0,0,0,0.08)' },
        '&:hover fieldset': { borderColor: 'rgba(0,0,0,0.18)' },
        '&.Mui-focused fieldset': { borderColor: MAIN_BG_COLOR },
    },
} as const;

const AgendaDemoPage: React.FC = () => {
    const [searchParams] = useSearchParams();
    const isVerificationIntent = searchParams.get('intent') === VERIFICATION_INTENT;

    const [name, setName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [company, setCompany] = useState('');
    const [role, setRole] = useState('');
    const [message, setMessage] = useState(() =>
        isVerificationIntent ? VERIFICATION_MESSAGE_PREFILL : ''
    );
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState('');

    const contactSource = useMemo(
        () => (isVerificationIntent ? 'Pide una verificación' : CONTACT_SOURCE),
        [isVerificationIntent]
    );

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        const fullName = [name.trim(), lastName.trim()].filter(Boolean).join(' ');
        const companyName = company.trim();
        const subject = isVerificationIntent
            ? companyName
                ? `Solicitud de verificación Podscope de ${companyName}`
                : 'Solicitud de verificación Podscope'
            : undefined;

        try {
            await sendContactEmail({
                name: fullName,
                firstName: name,
                lastName,
                email,
                company,
                role,
                message: message.trim(),
                subject,
            });
            setSuccess(true);
            analytics.track(AnalyticsEvent.SUBMIT_CONTACT_FORM, {
                source: contactSource,
                intent: isVerificationIntent ? VERIFICATION_INTENT : 'demo',
            });
            setName('');
            setLastName('');
            setEmail('');
            setCompany('');
            setRole('');
            setMessage(isVerificationIntent ? VERIFICATION_MESSAGE_PREFILL : '');
            window.setTimeout(() => setSuccess(false), 4500);
        } catch (err) {
            setError('Error al enviar el mensaje. Por favor intenta nuevamente.');
            console.error('EmailJS error:', err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{ width: '100%', background: PAGE.pageBg, minHeight: '70vh' }}>
            <Box
                sx={{
                    width: '100%',
                    background: MAIN_BG_COLOR,
                    py: { xs: 8, md: 10 },
                    px: { xs: PAGE.sectionPaddingX, md: 4 },
                }}
            >
                <Container maxWidth="lg">
                    <Typography
                        component="h1"
                        sx={{
                            fontFamily: PAGE.fontHeading,
                            fontWeight: 800,
                            fontSize: { xs: '2.5rem', md: '3.5rem' },
                            lineHeight: 1.15,
                            color: '#fff',
                            mb: 2,
                            maxWidth: 800,
                        }}
                    >
                        {isVerificationIntent ? 'Pide una verificación' : 'Agenda una demo'}
                    </Typography>
                    <Typography
                        sx={{
                            fontFamily: PAGE.fontBody,
                            fontWeight: 400,
                            fontSize: { xs: '1.0625rem', md: '1.125rem' },
                            lineHeight: 1.55,
                            color: 'rgba(255,255,255,0.72)',
                            maxWidth: 640,
                        }}
                    >
                        {isVerificationIntent
                            ? 'Cuéntanos qué mención o audiencia necesitas verificar. Te respondemos para acotar el alcance.'
                            : 'En 30 minutos te mostramos datos reales de tu programa y del ecosistema.'}
                    </Typography>
                </Container>
            </Box>

            <Box
                sx={{
                    width: '100%',
                    py: { xs: 5, md: 8 },
                    px: { xs: PAGE.sectionPaddingX, md: 4 },
                }}
            >
                <Container maxWidth="lg">
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', md: '1.05fr 0.95fr' },
                            gap: { xs: 5, md: 8 },
                            alignItems: 'start',
                        }}
                    >
                        <Box>
                            <Typography
                                component="h2"
                                sx={{
                                    fontFamily: PAGE.fontBody,
                                    fontWeight: 700,
                                    fontSize: '1.25rem',
                                    color: MAIN_BG_COLOR,
                                    mb: 1,
                                }}
                            >
                                Qué verás en la demo
                            </Typography>
                            <Typography
                                sx={{
                                    fontFamily: PAGE.fontBody,
                                    fontWeight: 400,
                                    fontSize: '0.9875rem',
                                    lineHeight: 1.55,
                                    color: 'rgba(0,0,0,0.55)',
                                    mb: 3.5,
                                    maxWidth: 520,
                                }}
                            >
                                Una sesión corta para entender cómo Podscope mide lo que se dice y
                                aparece en medios digitales, y qué puedes llevarte a tu operación.
                            </Typography>

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.75 }}>
                                {DEMO_BENEFITS.map(item => (
                                    <Box
                                        key={item.title}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'flex-start',
                                            gap: 1.5,
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                mt: 0.25,
                                                width: 22,
                                                height: 22,
                                                borderRadius: '50%',
                                                bgcolor: MAIN_BG_COLOR,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                flexShrink: 0,
                                            }}
                                        >
                                            <CheckIcon sx={{ color: '#fff', fontSize: 14 }} />
                                        </Box>
                                        <Box>
                                            <Typography
                                                sx={{
                                                    fontFamily: PAGE.fontBody,
                                                    fontWeight: 600,
                                                    fontSize: '1rem',
                                                    lineHeight: 1.35,
                                                    color: MAIN_BG_COLOR,
                                                    mb: 0.5,
                                                }}
                                            >
                                                {item.title}
                                            </Typography>
                                            <Typography
                                                sx={{
                                                    fontFamily: PAGE.fontBody,
                                                    fontWeight: 400,
                                                    fontSize: '0.9375rem',
                                                    lineHeight: 1.5,
                                                    color: 'rgba(0,0,0,0.58)',
                                                }}
                                            >
                                                {item.description}
                                            </Typography>
                                        </Box>
                                    </Box>
                                ))}
                            </Box>
                        </Box>

                        <Box
                            component="form"
                            onSubmit={handleSubmit}
                            sx={{
                                background: '#fff',
                                borderRadius: '16px',
                                p: { xs: 3, md: 4 },
                                boxShadow: '0 8px 32px rgba(0,0,0,0.06)',
                                border: '0.5px solid rgba(0,0,0,0.06)',
                            }}
                        >
                            <Typography
                                component="h2"
                                sx={{
                                    fontFamily: PAGE.fontBody,
                                    fontWeight: 700,
                                    fontSize: '1.25rem',
                                    color: MAIN_BG_COLOR,
                                    mb: 0.75,
                                }}
                            >
                                Cuéntanos sobre ti
                            </Typography>
                            <Typography
                                sx={{
                                    fontFamily: PAGE.fontBody,
                                    fontWeight: 400,
                                    fontSize: '0.875rem',
                                    color: 'rgba(0,0,0,0.5)',
                                    mb: 3,
                                    lineHeight: 1.45,
                                }}
                            >
                                Completa el formulario y te contactamos para agendar la demo.
                            </Typography>

                            {success && (
                                <Alert severity="success" sx={{ mb: 2 }}>
                                    ¡Mensaje enviado con éxito! Te contactaremos pronto.
                                </Alert>
                            )}
                            {error && (
                                <Alert severity="error" sx={{ mb: 2 }}>
                                    {error}
                                </Alert>
                            )}

                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                                    gap: 2,
                                    mb: 2,
                                }}
                            >
                                <TextField
                                    required
                                    fullWidth
                                    label="Nombre"
                                    value={name}
                                    onChange={e => setName(e.target.value)}
                                    disabled={loading || success}
                                    sx={fieldSx}
                                />
                                <TextField
                                    required
                                    fullWidth
                                    label="Apellido"
                                    value={lastName}
                                    onChange={e => setLastName(e.target.value)}
                                    disabled={loading || success}
                                    sx={fieldSx}
                                />
                            </Box>
                            <TextField
                                required
                                fullWidth
                                type="email"
                                label="Email de trabajo"
                                value={email}
                                onChange={e => setEmail(e.target.value)}
                                disabled={loading || success}
                                sx={{ mb: 2, ...fieldSx }}
                            />
                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                                    gap: 2,
                                    mb: 2,
                                }}
                            >
                                <TextField
                                    required
                                    fullWidth
                                    label="Empresa"
                                    value={company}
                                    onChange={e => setCompany(e.target.value)}
                                    disabled={loading || success}
                                    sx={fieldSx}
                                />
                                <TextField
                                    required
                                    fullWidth
                                    label="Cargo en la empresa"
                                    value={role}
                                    onChange={e => setRole(e.target.value)}
                                    disabled={loading || success}
                                    sx={fieldSx}
                                />
                            </Box>
                            <TextField
                                required
                                fullWidth
                                multiline
                                rows={4}
                                label="¿Qué te gustaría ver?"
                                value={message}
                                onChange={e => setMessage(e.target.value)}
                                disabled={loading || success}
                                placeholder="Programa, marca, equipo…"
                                sx={{ mb: 3, ...fieldSx }}
                            />
                            <Button
                                type="submit"
                                fullWidth
                                variant="contained"
                                disabled={
                                    loading ||
                                    success ||
                                    !name ||
                                    !lastName ||
                                    !email ||
                                    !company ||
                                    !role ||
                                    !message
                                }
                                endIcon={
                                    loading ? (
                                        <CircularProgress size={18} sx={{ color: '#fff' }} />
                                    ) : undefined
                                }
                                sx={{
                                    background: MAIN_BG_COLOR,
                                    color: '#fff',
                                    borderRadius: '10px',
                                    py: 1.35,
                                    fontFamily: PAGE.fontBody,
                                    fontWeight: 600,
                                    fontSize: '0.9875rem',
                                    textTransform: 'none',
                                    boxShadow: 'none',
                                    '&:hover': {
                                        background: MAIN_BG_COLOR,
                                        boxShadow: 'none',
                                        opacity: 0.92,
                                    },
                                    '&.Mui-disabled': {
                                        background: 'rgba(0,0,0,0.12)',
                                        color: 'rgba(0,0,0,0.35)',
                                    },
                                }}
                            >
                                {loading ? 'Enviando...' : 'Agenda una demo'}
                            </Button>
                        </Box>
                    </Box>
                </Container>
            </Box>
        </Box>
    );
};

export default AgendaDemoPage;
